import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useMessage } from 'components/ui';
import {
  type AtlasGqlReportContext,
  useGetUserTemplateOptionsQuery,
  usePinTemplateMutation,
  useReorderPinnedTemplatesMutation,
  useUnpinTemplateMutation,
} from 'types/atlas-graphql';

import { buildUserTemplateOptionMenuItems } from './buildUserTemplateOptionMenuItems';
import {
  type UserTemplateOptionFields,
  parseUserOptionMenuKey,
  swappedPinOrderIds,
  USER_TEMPLATE_OPTION_HIGHLIGHT_MS,
  USER_TEMPLATE_OPTIONS_QUERY_NAME,
} from './utils';

type Args = {
  enabled: boolean;
  contexts: AtlasGqlReportContext[];
  shareInHorizonOrganizationCount: number;
  getDisabledReason?: (option: UserTemplateOptionFields) => string | undefined;
};

// Only what the service said. A transport failure's message ("Failed to fetch")
// is no reason a user can act on, so that case takes the localized fallback.
const getServiceErrorMessage = (error: unknown): string =>
  CombinedGraphQLErrors.is(error) ? (error.errors[0]?.message ?? '') : '';

export const useUserTemplateOptionsMenu = ({
  enabled,
  contexts,
  shareInHorizonOrganizationCount,
  getDisabledReason,
}: Args) => {
  const { t } = useTranslation();
  const message = useMessage();
  const suppressNextMenuClickRef = useRef(false);

  const { data, error, refetch } = useGetUserTemplateOptionsQuery({
    variables: { contexts },
    skip: !enabled || !contexts.length,
  });

  const refetchQueries = [USER_TEMPLATE_OPTIONS_QUERY_NAME];

  const [pinTemplate] = usePinTemplateMutation({
    refetchQueries,
    awaitRefetchQueries: true,
  });
  const [unpinTemplate] = useUnpinTemplateMutation({
    refetchQueries,
    awaitRefetchQueries: true,
  });
  const [reorderPinnedTemplates] = useReorderPinnedTemplatesMutation({
    refetchQueries,
    awaitRefetchQueries: true,
  });

  const pinned = useMemo(() => data?.userTemplateOptions?.pinned ?? [], [data]);
  const recent = useMemo(() => data?.userTemplateOptions?.recent ?? [], [data]);
  // Only when there is nothing to show: a failed refetch after a pin or a report
  // keeps the last good list rather than replacing it with a retry row.
  const loadFailed = !!error && !data?.userTemplateOptions;
  const [highlightedOption, setHighlightedOption] = useState<{ id: string; token: number } | null>(
    null
  );

  const flashOption = useCallback((id: string) => {
    setHighlightedOption((current) => ({
      id,
      token: (current?.token ?? 0) + 1,
    }));
  }, []);

  useEffect(() => {
    if (!highlightedOption) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      setHighlightedOption(null);
    }, USER_TEMPLATE_OPTION_HIGHLIGHT_MS);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [highlightedOption]);

  // Swallow only the click that triggered the action -- rc-menu reads it as a menu
  // activation and antd's Dropdown as a dismissal. Holding the flag for the mutation
  // would leave the dropdown undismissable while the request is in flight.
  const suppressMenuClick = useCallback(() => {
    suppressNextMenuClickRef.current = true;
    window.setTimeout(() => {
      suppressNextMenuClickRef.current = false;
    }, 0);
  }, []);

  const handlePin = useCallback(
    async (option: UserTemplateOptionFields) => {
      suppressMenuClick();
      try {
        await pinTemplate({ variables: { id: option.id, contexts } });
        flashOption(option.id);
      } catch (error) {
        // Pin rejections are actionable and the service owns the reason -- the
        // pin limit above all, whose value is a service-side setting the client
        // must not guess at. Show what the service said; fall back to the
        // generic message only when it said nothing useful.
        message.error({
          content:
            getServiceErrorMessage(error) ||
            t('reports.pinnedReportsUpdateFailed', 'Unable to update pinned reports.'),
        });
      }
    },
    [contexts, flashOption, message, pinTemplate, suppressMenuClick, t]
  );

  const handleUnpin = useCallback(
    async (option: UserTemplateOptionFields) => {
      suppressMenuClick();
      try {
        await unpinTemplate({ variables: { id: option.id, contexts } });
        flashOption(option.id);
      } catch {
        message.error({
          content: t('reports.pinnedReportsUpdateFailed', 'Unable to update pinned reports.'),
        });
      }
    },
    [contexts, flashOption, message, suppressMenuClick, t, unpinTemplate]
  );

  const handleMove = useCallback(
    async (index: number, direction: 'up' | 'down') => {
      suppressMenuClick();
      const ids = swappedPinOrderIds(
        pinned.map((option) => option.id),
        index,
        direction
      );
      if (ids.every((id, i) => id === pinned[i]?.id)) {
        return;
      }
      try {
        await reorderPinnedTemplates({ variables: { ids, contexts } });
        const movedId = pinned[index]?.id;
        if (movedId) {
          flashOption(movedId);
        }
      } catch {
        message.error({
          content: t('reports.pinnedReportsUpdateFailed', 'Unable to update pinned reports.'),
        });
      }
    },
    [contexts, flashOption, message, pinned, reorderPinnedTemplates, suppressMenuClick, t]
  );

  const menuItems = useMemo(
    () =>
      enabled
        ? buildUserTemplateOptionMenuItems({
            pinned,
            recent,
            t,
            shareInHorizonOrganizationCount,
            getDisabledReason,
            onPin: handlePin,
            onUnpin: handleUnpin,
            onMove: handleMove,
            highlightedOptionId: highlightedOption?.id,
            highlightToken: highlightedOption?.token,
            loadFailed,
          })
        : [],
    [
      enabled,
      getDisabledReason,
      handleMove,
      handlePin,
      handleUnpin,
      highlightedOption,
      loadFailed,
      pinned,
      recent,
      shareInHorizonOrganizationCount,
      t,
    ]
  );

  const findOption = useCallback(
    (key: string): UserTemplateOptionFields | undefined => {
      const id = parseUserOptionMenuKey(key);
      if (!id) {
        return undefined;
      }
      return [...pinned, ...recent].find((option) => option.id === id);
    },
    [pinned, recent]
  );

  const isMenuClickSuppressed = useCallback(() => suppressNextMenuClickRef.current, []);

  const retryLoad = useCallback(() => {
    // Handled like a pin click so the dropdown stays open and the result lands in
    // place. A second failure re-renders the retry row through `error`.
    suppressMenuClick();
    refetch().catch(() => undefined);
  }, [refetch, suppressMenuClick]);

  return {
    menuItems,
    findOption,
    isMenuClickSuppressed,
    retryLoad,
  };
};
